/**
 * useNodeLabelFiltering Hook
 *
 * React Flow-specific hook for filtering nodes and edges by labels.
 * Applies opacity styling to filtered-out elements rather than hiding them.
 *
 * This hook is specifically for the mindmap canvas and handles:
 * - Attaching labels to node data
 * - Applying opacity (0.4) to filtered-out nodes
 * - Applying opacity to edges between filtered nodes
 * - Performance optimization via data reference caching
 */

import { useMemo, useRef } from 'react'
import type { Node, Edge } from '@xyflow/react'
import type { ProjectLabel } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { useVisibleNodeIds, buildParentMap, buildNodeLabelMap } from './useVisibleNodeIds'

/**
 * Configuration for the useNodeLabelFiltering hook
 */
interface UseNodeLabelFilteringConfig {
  /** Styled nodes from MindMapFlow */
  nodes: Node<ReactFlowNodeData>[]
  /** Styled edges from MindMapFlow */
  edges: Edge[]
  /** Currently selected filter label IDs */
  selectedFilterLabels: string[]
  /** Function to get labels for a node ID */
  getNodeLabels: (nodeId: string) => ProjectLabel[]
  /** Version number that changes when labels are updated (for cache invalidation) */
  nodeLabelsVersion: number
}

/**
 * Return type for the useNodeLabelFiltering hook
 */
interface UseNodeLabelFilteringReturn {
  /** Nodes with labels attached and filter styling applied */
  nodesWithLabels: Node<ReactFlowNodeData>[]
  /** Edges with filter styling applied */
  filteredEdges: Edge[]
}

/**
 * Custom hook for filtering nodes and edges by labels.
 *
 * When labels are selected, nodes with those labels (and their ancestors) remain
 * fully visible, while other nodes and edges are shown with reduced opacity.
 */
export function useNodeLabelFiltering({
  nodes,
  edges,
  selectedFilterLabels,
  getNodeLabels,
  nodeLabelsVersion,
}: UseNodeLabelFilteringConfig): UseNodeLabelFilteringReturn {
  // Build parent map for ancestor traversal
  const parentMap = useMemo(() => buildParentMap(edges), [edges])

  // Build node label map for filtering
  const nodeLabelMap = useMemo(() => {
    return buildNodeLabelMap(
      nodes.map((n) => n.id),
      getNodeLabels
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nodeLabelsVersion is used as cache invalidation trigger
  }, [nodes, getNodeLabels, nodeLabelsVersion])

  // Use shared visibility logic
  const { visibleNodeIds } = useVisibleNodeIds({
    nodeLabelMap,
    parentMap,
    allNodeIds: nodes.map((n) => n.id),
    selectedFilterLabels,
  })

  // Cache for processed nodes - preserves data references when only positions change
  // Key insight: React Flow re-renders nodes when their `data` prop changes (reference equality)
  // By keeping the same `data` object when labels/filters haven't changed, we prevent re-renders
  const nodeDataCache = useRef<
    Map<
      string,
      {
        data: ReactFlowNodeData & { labels: ProjectLabel[]; isFilteredOut: boolean }
        style: React.CSSProperties
        labelIds: string
        isFilteredOut: boolean
        cachedLabelsVersion: number
        originalDataRef: ReactFlowNodeData
      }
    >
  >(new Map())

  // Sync labels from context to nodes and apply filter (with opacity, not hidden)
  // OPTIMIZED: Check cache BEFORE calling expensive getNodeLabels()
  const nodesWithLabels = useMemo(() => {
    const result = nodes.map((node) => {
      const isFilteredOut = selectedFilterLabels.length > 0 && !visibleNodeIds.has(node.id)

      // Check cache FIRST (before expensive getNodeLabels call)
      const cached = nodeDataCache.current.get(node.id)

      // Cache hit conditions:
      // 1. Node's data reference is the same (node wasn't modified)
      // 2. Labels version matches (no label changes globally)
      // 3. Filter state matches
      if (
        cached &&
        cached.originalDataRef === node.data &&
        cached.cachedLabelsVersion === nodeLabelsVersion &&
        cached.isFilteredOut === isFilteredOut
      ) {
        // Fast path: return cached data without calling getNodeLabels
        return {
          ...node,
          data: cached.data,
          style: cached.style,
        }
      }

      // Cache miss - need to fetch labels (expensive operation)
      const nodeLabels = getNodeLabels(node.id)
      const labelIds = nodeLabels.map((l) => l.id).join(',')

      // Check if we can reuse cached data objects even if node.data changed
      // This handles cases where node.data changed but labels didn't
      if (cached && cached.labelIds === labelIds && cached.isFilteredOut === isFilteredOut) {
        // Labels and filter state are the same - create new data with cached labels array
        // This preserves the labels array reference for downstream memoization
        const newData = {
          ...node.data,
          labels: cached.data.labels, // Reuse same labels array reference
          isFilteredOut,
        } as ReactFlowNodeData & { labels: ProjectLabel[]; isFilteredOut: boolean }

        // Update cache with new data reference but same labels
        nodeDataCache.current.set(node.id, {
          data: newData,
          style: cached.style,
          labelIds,
          isFilteredOut,
          cachedLabelsVersion: nodeLabelsVersion,
          originalDataRef: node.data,
        })

        return {
          ...node,
          data: newData,
          style: cached.style,
        }
      }

      // Full cache miss - create all new objects
      const newData = {
        ...node.data,
        labels: nodeLabels,
        isFilteredOut,
      } as ReactFlowNodeData & { labels: ProjectLabel[]; isFilteredOut: boolean }

      const newStyle = {
        ...node.style,
        opacity: isFilteredOut ? 0.4 : (node.style?.opacity ?? 1),
        transition: 'opacity 0.2s ease',
      }

      // Update cache
      nodeDataCache.current.set(node.id, {
        data: newData,
        style: newStyle,
        labelIds,
        isFilteredOut,
        cachedLabelsVersion: nodeLabelsVersion,
        originalDataRef: node.data,
      })

      return {
        ...node,
        data: newData,
        style: newStyle,
      }
    })

    // Clean up cache for removed nodes
    const currentIds = new Set(nodes.map((n) => n.id))
    for (const id of nodeDataCache.current.keys()) {
      if (!currentIds.has(id)) {
        nodeDataCache.current.delete(id)
      }
    }

    return result
  }, [nodes, getNodeLabels, nodeLabelsVersion, selectedFilterLabels, visibleNodeIds])

  // Apply filter transparency to edges
  const filteredEdges = useMemo(() => {
    if (selectedFilterLabels.length === 0) {
      return edges
    }

    return edges.map((edge) => {
      const sourceVisible = visibleNodeIds.has(edge.source)
      const targetVisible = visibleNodeIds.has(edge.target)
      const isEdgeFilteredOut = !sourceVisible || !targetVisible

      return {
        ...edge,
        data: {
          ...edge.data,
          isFilteredOut: isEdgeFilteredOut,
        },
        style: {
          ...edge.style,
          opacity: isEdgeFilteredOut ? 0.4 : (edge.style?.opacity ?? 1),
          transition: 'opacity 0.2s ease',
        },
      }
    })
  }, [edges, selectedFilterLabels, visibleNodeIds])

  return {
    nodesWithLabels,
    filteredEdges,
  }
}
